import { FormProvider, useForm, useFormContext } from "react-hook-form";

import FailedCross from "@/assets/svg/failedCross.svg?react";
import SucessCheck from "@/assets/svg/successCheck.svg?react";
import SucessSent from "@/assets/svg/successSent.svg?react";
import FormField from "@/components/ui/formField";
import {
  ForgotPasswordFormData,
  ForgotPasswordFormProps,
  ForgotPasswordResultType,
  ForgotPasswordStatus
} from "@/types/auth";

import { twMerge } from "tailwind-merge";

import { Button } from "../ui/button";
import { RESET_PASSWORD_RULES } from "./data";

export const RETRY_PASSWORD_DATA = {
  success: {
    icon: <SucessSent className="mx-auto mb-4" />,
    title: "Reset Link Sent",
    message:
      "We've re-sent a password reset link to your email. If you don't see it, please double check your spam folder."
  },
  error: {
    icon: <FailedCross className="mx-auto mb-4" />,
    title: "Sent Link Failed",
    message:
      "We couldn't send the reset link. Please go back to the login page to request a new one."
  }
};

const ForgotPasswordFinalResult = ({
  onReset,
  status
}: {
  onReset: () => void;
  status: "error" | "success";
}) => {
  return (
    <div className="flex flex-col gap-6">
      {RETRY_PASSWORD_DATA[status].icon}
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold text-txtHeading">
          {RETRY_PASSWORD_DATA[status].title}
        </h2>
        <p className="text-sm text-txtHeading">
          {RETRY_PASSWORD_DATA[status].message}
        </p>
      </div>
      <Button
        className="rounded-xl font-semibold text-base py-3"
        onClick={onReset}
      >
        Back to Sign In
      </Button>
    </div>
  );
};

const ForgotPasswordInitialResult = ({
  forgotPasswordStatus,
  onReset,
  mutateCount
}: ForgotPasswordResultType & { mutateCount: number }) => {
  if (forgotPasswordStatus === "error") {
    return <ForgotPasswordFinalResult status="error" onReset={onReset} />;
  } else if (mutateCount > 1 && forgotPasswordStatus === "success") {
    return <ForgotPasswordFinalResult status="success" onReset={onReset} />;
  }
  return (
    <div className="flex flex-col gap-6">
      <SucessCheck className="mx-auto mb-4" />
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold text-txtHeading">
          Reset Link Sent
        </h2>
        <p className="text-sm text-txtHeading">
          We've sent a password reset link to your email. If you don't see it,
          check your spam folder.
        </p>
      </div>
      <Button
        type="submit"
        className="rounded-xl font-semibold text-base py-3"
        disabled={forgotPasswordStatus === "pending"}
      >
        Didn't Get The Email? Resend Link
      </Button>
    </div>
  );
};

const ForgotPasswordEmailInput = ({
  forgotPasswordStatus
}: {
  forgotPasswordStatus: ForgotPasswordStatus;
}) => {
  const { register, formState, resetField, watch } =
    useFormContext<ForgotPasswordFormData>();
  const isSubmitButtonDisabled =
    !watch("email") || !formState.isValid || forgotPasswordStatus === "pending";
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-3xl font-semibold text-txtHeading">
          Reset Your Password
        </h2>
        <p className="text-sm text-txtHeading">
          Enter your email address, and we'll send a reset link if your account
          exists.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <FormField
          data-testid="reset-email-input"
          placeHolder="Email address"
          register={register}
          name="email"
          rules={RESET_PASSWORD_RULES.email}
          error={formState.errors.email?.message}
          resetValue={() => {
            resetField("email");
          }}
          autoCleanError={false}
          hasSpacingOnFocus={false}
          disabled={formState.isSubmitting}
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitButtonDisabled}
        className={twMerge(
          "rounded-xl font-semibold text-base py-3",
          isSubmitButtonDisabled && "bg-gray-300 disabled:opacity-70"
        )}
      >
        Send Reset Link
      </Button>
    </div>
  );
};

export const ForgotPasswordForm = ({
  setPageType,
  onSubmit,
  onReset,
  forgotPasswordStatus,
  mutateCount
}: ForgotPasswordFormProps) => {
  const formForgotPassword = useForm<ForgotPasswordFormData>({
    mode: "onChange",
    defaultValues: { email: "" }
  });
  const { reset, handleSubmit } = formForgotPassword;
  const resetFn = () => {
    reset();
    onReset();
    setPageType("login");
  };
  return (
    <FormProvider {...formForgotPassword}>
      <form className="mt-8" onSubmit={handleSubmit(onSubmit)}>
        {mutateCount < 1 ? (
          <ForgotPasswordEmailInput
            forgotPasswordStatus={forgotPasswordStatus}
          />
        ) : (
          <ForgotPasswordInitialResult
            mutateCount={mutateCount}
            forgotPasswordStatus={forgotPasswordStatus}
            onReset={resetFn}
          />
        )}
      </form>
      {forgotPasswordStatus !== "error" && mutateCount < 2 && (
        <button
          type="button"
          className="text-txtBody text-sm font-semibold hover:cursor-pointer mt-[18px]"
          onClick={resetFn}
        >
          Back to Sign In
        </button>
      )}
    </FormProvider>
  );
};

export default ForgotPasswordForm;
